import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ShieldCheck, Lock, Check, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { NdaModal } from "@/components/intake/NdaModal";
import { getCurrentNda, submitLead } from "@/lib/api";
import { getAttribution, trackEvent } from "@/lib/attribution";
import { AIRO } from "@/constants/testIds";
import { Seo } from "@/seo/Seo";
import { STATIC_META } from "@/seo/config";

const inputCls =
  "bg-[#0b0b0c] border-[#1f1f1f] text-white placeholder:text-zinc-600 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/60 h-12";
const areaCls =
  "bg-[#0b0b0c] border-[#1f1f1f] text-white placeholder:text-zinc-600 focus-visible:ring-cyan-400/40 focus-visible:border-cyan-400/60 min-h-[110px] resize-y";

const Field = ({ label, children, hint }) => (
  <div className="space-y-2.5">
    <Label className="mono-label text-zinc-400">{label}</Label>
    {children}
    {hint && <p className="text-xs text-zinc-600">{hint}</p>}
  </div>
);

const STEPS = [
  { key: "identity", label: "WHO WE'RE BUILDING WITH" },
  { key: "idea", label: "YOUR IDEA" },
  { key: "nda", label: "CONFIDENTIALITY" },
];

// Part 1 — the three structured classification questions (concise MC + Other).
const INQUIRY = [
  { field: "product_type", other: "product_type_other", tid: "inquiry-product-type",
    otherTid: "inquiry-product-other", title: "What are you trying to build?",
    help: "Pick the closest fit — we'll get specific in the next step.",
    otherPrompt: "Tell us what you're envisioning.",
    options: [
      ["website", "Website / Web Experience"],
      ["application", "Application / Digital Product"],
      ["ai", "AI Product / Agent"],
      ["automation", "Automation / Workflow"],
    ] },
  { field: "audience_type", other: "audience_type_other", tid: "inquiry-audience-type",
    otherTid: "inquiry-audience-other", title: "Who is it for?",
    help: "The people or organizations who'll use it.",
    otherPrompt: "Who is it specifically for?",
    options: [
      ["individuals", "Individuals / Consumers"],
      ["smb", "Small Businesses / Startups"],
      ["enterprise", "Organizations / Enterprise"],
      ["members", "Customers / Members / Teams"],
    ] },
  { field: "problem_type", other: "problem_type_other", tid: "inquiry-problem-type",
    otherTid: "inquiry-problem-other", title: "What problem are you trying to solve?",
    help: "The main outcome you're after.",
    otherPrompt: "What problem are you trying to solve?",
    options: [
      ["automate", "Automate / Simplify a Process"],
      ["revenue", "Get More Customers / Increase Revenue"],
      ["experience", "Improve Customer / User Experience"],
      ["modernize", "Build or Modernize a Digital Product"],
    ] },
];

const empty = {
  party_type: "individual",
  party_legal_name: "",
  company_legal_name: "",
  entity_type: "",
  formation_jurisdiction: "",
  title_role: "",
  email: "",
  phone: "",
  product_type: "",
  product_type_other: "",
  audience_type: "",
  audience_type_other: "",
  problem_type: "",
  problem_type_other: "",
  typed_signature: "",
  website: "",
};

export default function Intake() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [nda, setNda] = useState(null);
  const [ndaOpen, setNdaOpen] = useState(false);
  const [ndaViewed, setNdaViewed] = useState(false);
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [authorityConfirmed, setAuthorityConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    trackEvent("questionnaire_started", {}, { once: "start" });
    getCurrentNda().then(setNda).catch(() => toast.error("Could not load the NDA. Please refresh."));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const isCompany = form.party_type === "company";

  const legalName = isCompany
    ? (form.company_legal_name || "[COMPANY NAME]")
    : (form.party_legal_name || "[LEGAL NAME]");

  const consentLanguage = useMemo(() => {
    if (!nda) return "";
    return `By checking the box below, ${legalName} acknowledges and agrees to the AIROTECH MVP Mutual Non-Disclosure Agreement, version ${nda.version}, dated ${nda.effective_date}. By selecting this box and submitting this assessment, I intend to electronically sign and enter into the agreement identified above.`;
  }, [nda, legalName]);

  const authorityLanguage =
    "I represent that I am authorized to enter into this agreement on behalf of the entity identified above.";

  const step1Valid =
    form.party_legal_name.trim().length > 1 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.phone.trim().length > 4 &&
    (!isCompany || form.company_legal_name.trim().length > 1);

  const step2Valid = INQUIRY.every((q) =>
    form[q.field] && (form[q.field] !== "other" || form[q.other].trim().length > 1));

  const submitReady =
    ndaAccepted &&
    form.typed_signature.trim().length > 1 &&
    (!isCompany || authorityConfirmed) &&
    !!nda;

  const goNext = () => {
    if (step === 0 && !step1Valid) return toast.error("Please complete the required identity fields.");
    if (step === 1 && !step2Valid) return toast.error("Please answer all three questions.");
    const nextStep = Math.min(step + 1, STEPS.length - 1);
    trackEvent("questionnaire_step", { from: STEPS[step].key, to: STEPS[nextStep].key, step: nextStep + 1 });
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  const handleSubmit = async () => {
    if (!submitReady) return;
    setSubmitting(true);
    try {
      await submitLead({
        party_type: form.party_type,
        party_legal_name: form.party_legal_name.trim(),
        company_legal_name: isCompany ? form.company_legal_name.trim() : "",
        entity_type: isCompany ? form.entity_type.trim() : "",
        formation_jurisdiction: isCompany ? form.formation_jurisdiction.trim() : "",
        title_role: isCompany ? form.title_role.trim() : "",
        email: form.email.trim(),
        phone: form.phone.trim(),
        product_responses: {
          product_type: form.product_type,
          product_type_other: form.product_type === "other" ? form.product_type_other.trim() : "",
          audience_type: form.audience_type,
          audience_type_other: form.audience_type === "other" ? form.audience_type_other.trim() : "",
          problem_type: form.problem_type,
          problem_type_other: form.problem_type === "other" ? form.problem_type_other.trim() : "",
        },
        nda_document_id: nda.id,
        nda_version: nda.version,
        nda_accepted: ndaAccepted,
        authority_confirmed: isCompany ? authorityConfirmed : false,
        typed_signature: form.typed_signature.trim(),
        consent_language: consentLanguage,
        authority_language: isCompany ? authorityLanguage : "",
        website: form.website,
        attribution: getAttribution(),
      });
      trackEvent("questionnaire_completed", { party_type: form.party_type }, { once: "lead" });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "auto" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const trans = reduce
    ? {}
    : { initial: { opacity: 0, x: 24 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -24 }, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } };

  if (done) return <Success onContinue={() => navigate("/continue")} />;

  return (
    <div data-testid={AIRO.intakeRoot} className="min-h-screen bg-[#050505] text-white">
      <Seo path="/start" title={STATIC_META["/start"].title} description={STATIC_META["/start"].description} />
      {/* Header */}
      <header className="border-b border-[#141414] sticky top-0 z-40 bg-black/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <button onClick={() => navigate("/")} aria-label="AIROTECH home">
            <img src="/airotech-mark.png" alt="AIROTECH MVP" className="h-8 w-auto" />
          </button>
          <div data-testid={AIRO.intakeProgress} className="mono-label text-cyan-400">
            0{step + 1} / {STEPS[step].label}
          </div>
        </div>
        <div className="h-0.5 bg-[#141414]">
          <div className="h-full bg-primary transition-[width] duration-500"
               style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        {/* honeypot */}
        <input
          data-testid={AIRO.fieldWebsiteHoney}
          type="text" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={set("website")}
          className="absolute -left-[9999px] opacity-0 h-0 w-0" aria-hidden="true"
        />

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.section key="identity" {...trans}>
              <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-4xl mb-3">
                First, tell us who we're building with.
              </h1>
              <p className="text-zinc-400 mb-10">This helps us represent your idea correctly.</p>

              <div className="grid grid-cols-2 gap-3 mb-9">
                {[["individual", "Individual", AIRO.identityIndividual], ["company", "Company / Organization", AIRO.identityCompany]].map(
                  ([val, label, tid]) => (
                    <button
                      key={val}
                      data-testid={tid}
                      onClick={() => setForm((f) => ({ ...f, party_type: val }))}
                      className={`rounded-xl border p-5 text-left transition-colors duration-200 ${
                        form.party_type === val ? "border-cyan-400/60 bg-cyan-400/[0.06]" : "border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#2a2a2a]"
                      }`}
                    >
                      <div className={`font-heading text-base ${form.party_type === val ? "text-cyan-300" : "text-white"}`}>{label}</div>
                    </button>
                  )
                )}
              </div>

              <div className="space-y-6">
                <AnimatePresence>
                  {isCompany && (
                    <motion.div
                      initial={reduce ? {} : { opacity: 0, height: 0 }}
                      animate={reduce ? {} : { opacity: 1, height: "auto" }}
                      exit={reduce ? {} : { opacity: 0, height: 0 }}
                      className="space-y-6 overflow-hidden"
                    >
                      <Field label="Legal Company Name *">
                        <Input data-testid={AIRO.fieldCompanyName} className={inputCls} value={form.company_legal_name} onChange={set("company_legal_name")} placeholder="Acme Technologies, Inc." />
                      </Field>
                      <div className="grid md:grid-cols-2 gap-6">
                        <Field label="Entity Type">
                          <Input data-testid={AIRO.fieldEntityType} className={inputCls} value={form.entity_type} onChange={set("entity_type")} placeholder="LLC, C-Corp, etc." />
                        </Field>
                        <Field label="State / Country of Formation">
                          <Input data-testid={AIRO.fieldFormation} className={inputCls} value={form.formation_jurisdiction} onChange={set("formation_jurisdiction")} placeholder="Delaware, USA" />
                        </Field>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <Field label={isCompany ? "Your Full Legal Name *" : "Full Legal Name *"}>
                  <Input data-testid={AIRO.fieldPartyName} className={inputCls} value={form.party_legal_name} onChange={set("party_legal_name")} placeholder="Jane Doe" />
                </Field>
                {isCompany && (
                  <Field label="Your Title / Role">
                    <Input data-testid={AIRO.fieldTitle} className={inputCls} value={form.title_role} onChange={set("title_role")} placeholder="Founder & CEO" />
                  </Field>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label="Email *">
                    <Input data-testid={AIRO.fieldEmail} type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  </Field>
                  <Field label="Phone *">
                    <Input data-testid={AIRO.fieldPhone} className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" />
                  </Field>
                </div>
              </div>
            </motion.section>
          )}

          {step === 1 && (
            <motion.section key="idea" {...trans}>
              <h1 className="font-heading font-extrabold tracking-tighter text-3xl md:text-4xl mb-3">
                Tell us what you're trying to build.
              </h1>
              <p className="text-zinc-400 mb-10">Three quick questions — no technical language required. We'll go deeper in your personalized assessment.</p>

              <div className="space-y-11">
                {INQUIRY.map((q) => {
                  const selected = form[q.field];
                  const isOther = selected === "other";
                  return (
                    <div key={q.field} data-testid={q.tid}>
                      <Label className="mono-label text-zinc-400">{q.title}</Label>
                      <p className="text-sm text-zinc-600 mt-1.5 mb-4">{q.help}</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {[...q.options, ["other", "Other"]].map(([val, label]) => {
                          const sel = selected === val;
                          return (
                            <button
                              key={val}
                              type="button"
                              data-testid={`${q.tid}-${val}`}
                              onClick={() => setForm((f) => ({ ...f, [q.field]: val }))}
                              aria-pressed={sel}
                              className={`text-left rounded-xl border p-4 transition-colors duration-200 ${
                                sel ? "border-cyan-400/60 bg-cyan-400/[0.06] text-cyan-100" : "border-[#1f1f1f] bg-[#0a0a0a] text-zinc-300 hover:border-[#2a2a2a]"
                              }`}
                            >
                              <span className="font-heading text-[15px]">{label}</span>
                            </button>
                          );
                        })}
                      </div>
                      <AnimatePresence>
                        {isOther && (
                          <motion.div
                            initial={reduce ? {} : { opacity: 0, height: 0 }}
                            animate={reduce ? {} : { opacity: 1, height: "auto" }}
                            exit={reduce ? {} : { opacity: 0, height: 0 }}
                            className="overflow-hidden"
                          >
                            <Input
                              data-testid={q.otherTid}
                              className={`${inputCls} mt-4`}
                              value={form[q.other]}
                              onChange={set(q.other)}
                              placeholder={q.otherPrompt}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.section>
          )}

          {step === 2 && (
            <motion.section key="nda" {...trans}>
              <div className="rounded-2xl border border-[#1f1f1f] bg-[#0a0a0a] p-7 md:p-9 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-10 w-10 rounded-full bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center">
                    <Lock className="h-4 w-4 text-cyan-400" />
                  </span>
                  <h1 className="font-heading font-bold text-2xl md:text-3xl">Your idea is confidential.</h1>
                </div>
                <p className="text-zinc-400 leading-relaxed">
                  Your product information may contain confidential or proprietary information. Before submitting your
                  idea, please review the AIROTECH MVP Mutual Non-Disclosure Agreement. Nothing confidential is stored
                  until you accept and submit.
                </p>
                <button
                  data-testid={AIRO.viewNda}
                  onClick={() => { setNdaOpen(true); setNdaViewed(true); }}
                  className="mt-6 inline-flex items-center gap-2 mono-label border border-[#232323] px-6 py-3.5 rounded-full hover:border-cyan-400/50 hover:text-cyan-300 transition-colors duration-200"
                >
                  <ShieldCheck className="h-4 w-4" /> View Mutual NDA
                </button>
              </div>

              {/* Acceptance */}
              <div className="rounded-2xl border border-[#1f1f1f] bg-[#0a0a0a] p-7 md:p-9 space-y-6">
                <p className="text-sm text-zinc-400 leading-relaxed">{consentLanguage}</p>

                <label className="flex items-start gap-3.5 cursor-pointer">
                  <Checkbox
                    data-testid={AIRO.ndaAcceptCheckbox}
                    checked={ndaAccepted}
                    onCheckedChange={(v) => setNdaAccepted(!!v)}
                    className="mt-0.5 border-[#2a2a2a] data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground"
                  />
                  <span className="text-sm text-zinc-200">I have reviewed and agree to the AIROTECH MVP Mutual NDA.</span>
                </label>

                {isCompany && (
                  <label className="flex items-start gap-3.5 cursor-pointer">
                    <Checkbox
                      data-testid={AIRO.authorityCheckbox}
                      checked={authorityConfirmed}
                      onCheckedChange={(v) => setAuthorityConfirmed(!!v)}
                      className="mt-0.5 border-[#2a2a2a] data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground"
                    />
                    <span className="text-sm text-zinc-200">{authorityLanguage}</span>
                  </label>
                )}

                <Field label="Type your full legal name to sign *" hint="This serves as your electronic signature.">
                  <Input data-testid={AIRO.fieldSignature} className={inputCls} value={form.typed_signature} onChange={set("typed_signature")} placeholder={isCompany ? form.party_legal_name || "Your full legal name" : form.party_legal_name || "Your full legal name"} />
                </Field>

                <button
                  data-testid={AIRO.submitIdea}
                  onClick={handleSubmit}
                  disabled={!submitReady || submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium px-7 py-4 rounded-full mono-label disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#00b8cc] transition-colors duration-200"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <><Check className="h-4 w-4" /> Submit My Idea</>}
                </button>
                {!ndaViewed && <p className="text-xs text-zinc-600 text-center">Tip: review the NDA above before accepting.</p>}
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* Nav buttons */}
        {step < 2 && (
          <div className="mt-12 flex items-center justify-between">
            <button
              data-testid={AIRO.stepBack}
              onClick={step === 0 ? () => navigate("/") : goBack}
              className="inline-flex items-center gap-2 mono-label text-zinc-500 hover:text-white transition-colors duration-200"
            >
              <ArrowLeft className="h-4 w-4" /> {step === 0 ? "Exit" : "Back"}
            </button>
            <button
              data-testid={AIRO.stepNext}
              onClick={goNext}
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium px-7 py-3.5 rounded-full mono-label hover:bg-[#00b8cc] transition-colors duration-200"
            >
              Continue <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </div>
        )}
        {step === 2 && (
          <div className="mt-8">
            <button
              data-testid={AIRO.stepBack}
              onClick={goBack}
              className="inline-flex items-center gap-2 mono-label text-zinc-500 hover:text-white transition-colors duration-200"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          </div>
        )}
      </main>

      <NdaModal open={ndaOpen} onOpenChange={setNdaOpen} nda={nda} />
    </div>
  );
}

const Success = ({ onContinue }) => (
  <div data-testid={AIRO.successScreen} className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 relative overflow-hidden">
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[720px] rounded-full bg-cyan-500/[0.08] blur-[130px] pointer-events-none" />
    <motion.div
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative max-w-xl text-center"
    >
      <span className="inline-flex h-14 w-14 rounded-full bg-cyan-400/10 border border-cyan-400/25 items-center justify-center mb-8">
        <CheckCircle2 className="h-6 w-6 text-cyan-400" />
      </span>
      <h1 className="font-heading font-extrabold tracking-tighter text-5xl md:text-6xl text-glow">WE'VE GOT IT.</h1>
      <p className="mt-6 text-lg text-zinc-300 leading-relaxed">
        Thank you for trusting AIROTECH with your idea. We've received your initial product information and
        recorded your confidentiality agreement. We're preparing the next step now.
      </p>
      <p className="mt-6 mono-label text-cyan-400">CHECK YOUR EMAIL TO CONTINUE</p>
      <button
        data-testid={AIRO.continueAssessment}
        onClick={onContinue}
        className="mt-9 inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium px-8 py-4 rounded-full mono-label hover:bg-[#00b8cc] transition-colors duration-200"
      >
        Continue My Assessment <ArrowRight className="h-4 w-4" />
      </button>
    </motion.div>
  </div>
);
